// Filtros do PostHog: ruído de error tracking e privacidade do session replay.

const EXTENSION_PROTOCOLS = ['chrome-extension://', 'moz-extension://', 'safari-extension://', 'safari-web-extension://']

const RESIZE_OBSERVER_MESSAGES = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications'
]

const NETWORK_ABORT_MESSAGES = ['The user aborted a request', 'Load failed', 'Failed to fetch']

const getFrames = exception => exception?.stacktrace?.frames || []

const frameUrl = frame => frame?.filename || frame?.abs_path || ''

const isResizeObserverNoise = (type, value) =>
  RESIZE_OBSERVER_MESSAGES.some(message => value.includes(message) || type.includes(message))

const isScriptErrorWithoutStack = (value, frames) => value.trim() === 'Script error.' && frames.length === 0

const isExtensionOnly = frames =>
  frames.length > 0 && frames.every(frame => EXTENSION_PROTOCOLS.some(protocol => frameUrl(frame).startsWith(protocol)))

const isNetworkAbort = (type, value) =>
  type === 'AbortError' || NETWORK_ABORT_MESSAGES.some(message => value === message || value === `TypeError: ${message}`)

const isNoisyException = exception => {
  const type = exception?.type || ''
  const value = exception?.value || ''
  const frames = getFrames(exception)

  return (
    isResizeObserverNoise(type, value) ||
    isScriptErrorWithoutStack(value, frames) ||
    isExtensionOnly(frames) ||
    isNetworkAbort(type, value)
  )
}

/**
 * before_send: descarta eventos `$exception` que são ruído conhecido do navegador
 * (ResizeObserver, "Script error." sem stack, erros vindos só de extensões, abortos de rede).
 * Qualquer outro evento passa intacto, inclusive `$snapshot` do session replay.
 * @param {object|null} event evento do posthog-js
 * @returns {object|null} o próprio evento, ou null para descartar
 */
export const dropNoisyExceptions = event => {
  if (!event || event.event !== '$exception') return event

  const exceptionList = event.properties?.$exception_list
  if (!Array.isArray(exceptionList) || exceptionList.length === 0) return event

  // A primeira exceção da lista é a que foi lançada; as demais são `cause`.
  return isNoisyException(exceptionList[0]) ? null : event
}

const stripQueryAndHash = url => url.split(/[?#]/)[0]

// /token/<token>/<crid> leva o token de autenticação do aluno no path.
const redactAuthTokenPath = url => url.replace(/\/token\/[^/?#]+/, '/token/[redacted]')

/**
 * session_recording.maskCapturedNetworkRequestFn: tira query string, hash e o token
 * de autenticação do path das URLs gravadas no replay. No posthog-js 1.398 essa função
 * recebe tanto as requisições de rede quanto a URL da página (`_maskReplayUrl`: href do
 * evento Meta do rrweb, `$url_changed` e `$pageview` do replay), chamada com `{ name: url }`.
 * @param {object} request requisição capturada pelo posthog-js
 * @returns {object} a requisição com a URL sanitizada
 */
export const maskCapturedNetworkRequest = request => {
  if (!request || typeof request.name !== 'string') return request

  return { ...request, name: redactAuthTokenPath(stripQueryAndHash(request.name)) }
}
